/**
 * @file ChargesPage.tsx
 * @summary Page de l'application
 *
 * Route React Router déclarée dans `App.tsx`. Métadonnées SEO injectées via `<SEO/>` + prerender Puppeteer (cf. vite.config).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useState, useMemo, useCallback, useEffect } from "react";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { buildSerpImageGraph, SERP_IMAGE_URLS } from "@/lib/serp-images";
import { Link } from "react-router-dom";
import { ArrowLeft, Info, Copy, RotateCcw, Save, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  suggestLoad, BARS, PLATES, PLATE_COLORS, FORMULAS, MOVEMENTS,
  PCT_COLS, FIXED_COLS, MOVEMENT_DEFAULT_FORMULA, type SuggestResult,
} from "@/lib/charges";
import { useToast } from "@/hooks/use-toast";

/* ── PR Calculator ─────────────────────────────── */
function PRCalculator({ onUsePR }: { onUsePR: (pr: number) => void }) {
  const [movement, setMovement] = useState(MOVEMENTS[0]);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const defaultFormula = MOVEMENT_DEFAULT_FORMULA[movement] ?? 0;
  const [formulaIdx, setFormulaIdx] = useState(defaultFormula);
  const [formulaManuallySet, setFormulaManuallySet] = useState(false);
  const { toast } = useToast();

  // Auto-switch formula when movement changes (unless user manually chose one)
  useEffect(() => {
    if (!formulaManuallySet) {
      setFormulaIdx(MOVEMENT_DEFAULT_FORMULA[movement] ?? 0);
    }
  }, [movement, formulaManuallySet]);

  const numW = parseFloat(weight) || 0;
  const numR = parseInt(reps) || 0;

  const estimated = useMemo(() => {
    if (numW <= 0 || numR <= 0) return 0;
    if (numR === 1) return numW;
    return Math.round(FORMULAS[formulaIdx].fn(numW, numR) * 10) / 10;
  }, [numW, numR, formulaIdx]);

  // localStorage
  const storageKey = `pr_${movement}`;
  const [savedPR, setSavedPR] = useState<number | null>(null);

  useEffect(() => {
    const v = localStorage.getItem(storageKey);
    setSavedPR(v ? parseFloat(v) : null);
  }, [storageKey]);

  const savePR = () => {
    if (estimated > 0) {
      localStorage.setItem(storageKey, String(estimated));
      setSavedPR(estimated);
      toast({ title: "PR enregistré", description: `${movement} : ${estimated} kg` });
    }
  };

  const loadPR = () => {
    if (savedPR) {
      onUsePR(savedPR);
      toast({ title: "PR chargé", description: `${savedPR} kg appliqué aux tableaux` });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 mb-10">
      <h2 className="text-2xl md:text-3xl text-foreground mb-2">Estimer mon PR</h2>
      <p className="text-sm text-muted-foreground font-body normal-case mb-6 max-w-2xl">
        Ton <strong className="text-foreground">PR (Personal Record)</strong> est la charge maximale que tu peux soulever en une seule répétition.
        Si tu ne le connais pas, renseigne un poids et un nombre de reps réalisés : la formule estimera ton PR théorique.
        Plus le nombre de reps est bas (≤ 5), plus l'estimation est fiable
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Movement */}
        <div>
          <label className="text-xs font-medium text-muted-foreground font-body normal-case mb-1 block">Mouvement</label>
          <div className="relative">
            <select
              value={movement}
              onChange={(e) => setMovement(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 pr-8 text-sm font-body normal-case appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {MOVEMENTS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Weight */}
        <div>
          <label className="text-xs font-medium text-muted-foreground font-body normal-case mb-1 block">Poids soulevé (kg)</label>
          <Input type="number" step="0.5" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Ex : 80" />
        </div>

        {/* Reps */}
        <div>
          <label className="text-xs font-medium text-muted-foreground font-body normal-case mb-1 block">Nombre de reps</label>
          <Input type="number" min="1" max="20" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="Ex : 5" />
        </div>

        {/* Formula */}
        <div>
          <label className="text-xs font-medium text-muted-foreground font-body normal-case mb-1 block">Formule</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <select
                value={formulaIdx}
                onChange={(e) => { setFormulaIdx(Number(e.target.value)); setFormulaManuallySet(true); }}
                className="w-full h-10 rounded-md border border-input bg-background px-3 pr-8 text-sm font-body normal-case appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {FORMULAS.map((f, i) => (
                  <option key={f.name} value={i}>
                    {f.name}{i === (MOVEMENT_DEFAULT_FORMULA[movement] ?? 0) ? " (la + pertinente)" : ""}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <Popover>
              <PopoverTrigger asChild><Info size={16} className="text-muted-foreground shrink-0 cursor-help" /></PopoverTrigger>
              <PopoverContent className="w-80 p-4 text-xs font-body normal-case space-y-3" side="bottom">
                <p className="font-semibold text-foreground text-sm">Quelle formule choisir ?</p>
                <p className="text-muted-foreground">
                  La formule est automatiquement adaptée au mouvement sélectionné. Tu peux la changer manuellement si besoin
                </p>
                <hr className="border-border" />
                {FORMULAS.map((f, i) => (
                  <div key={f.name} className={`rounded-md p-2 ${i === formulaIdx ? "bg-gold/10 border border-gold/20" : ""}`}>
                    <p className="font-semibold text-foreground">{f.name} {i === (MOVEMENT_DEFAULT_FORMULA[movement] ?? 0) && <span className="text-gold text-[10px]">★ recommandée</span>}</p>
                    <p className="text-muted-foreground mt-0.5">{f.description}</p>
                    <p className="text-muted-foreground mt-0.5 italic">{f.eq}</p>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {numR > 12 && (
        <p className="text-xs text-cta font-body normal-case mb-4">⚠ Estimation moins fiable au-delà de 10-12 reps</p>
      )}

      {estimated > 0 && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="bg-gold/10 border border-gold/20 rounded-lg px-5 py-3">
            <span className="text-xs text-muted-foreground font-body normal-case">PR estimé</span>
            <p className="text-3xl text-gold font-heading">{estimated} KG</p>
            <span className="text-xs text-muted-foreground font-body normal-case">Formule : {FORMULAS[formulaIdx].name}</span>
          </div>
          <div className="flex flex-col gap-2">
            <Button size="sm" className="bg-cta hover:bg-cta/90 text-cta-foreground font-body normal-case" onClick={() => onUsePR(estimated)}>
              Utiliser comme PR
            </Button>
            {savedPR && (
              <Button size="sm" variant="ghost" className="font-body normal-case text-gold" onClick={loadPR}>
                Charger PR sauvé ({savedPR} kg)
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Plate Cell ────────────────────────────────── */
function PlateCell({ result, barKg }: { result: SuggestResult; barKg: number }) {
  const copyPlates = () => {
    const lines = result.perSidePlates.map((p) => `${p.count}× ${p.label} (${p.color})`);
    const text = `Barre ${barKg} kg | Cible ${result.targetKg} kg → ${result.suggestedKg} kg\nPar côté : ${lines.length ? lines.join(" + ") : "Barre seule"}`;
    navigator.clipboard.writeText(text);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-full min-w-[80px] px-2 py-2.5 rounded-md bg-background border border-border hover:border-gold/40 hover:shadow-sm transition-all text-center cursor-pointer group">
          <span className="text-base font-semibold text-foreground font-body">{result.suggestedKg}<span className="text-xs ml-0.5">kg</span></span>
          {result.deltaKg > 0 && (
            <span className="block text-[10px] text-muted-foreground font-body normal-case">
              cible {result.targetKg} <span className="text-cta">(+{result.deltaKg})</span>
            </span>
          )}
          <Info size={10} className="mx-auto mt-0.5 text-muted-foreground/70 group-hover:text-gold transition-colors" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" side="top">
        <div className="space-y-3 font-body normal-case text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Cible théorique</span>
            <span className="font-semibold text-foreground">{result.targetKg} kg</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Charge proposée</span>
            <span className="font-semibold text-gold">{result.suggestedKg} kg</span>
          </div>
          {result.deltaKg > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Écart</span>
              <span className="text-cta font-medium">+{result.deltaKg} kg</span>
            </div>
          )}
          <hr className="border-border" />
          <p className="font-medium text-foreground text-xs">Par côté :</p>
          {result.perSidePlates.length === 0 ? (
            <p className="text-muted-foreground text-xs">Barre seule — aucune plate</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {result.perSidePlates.map((p) => (
                <span key={p.kg} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${PLATE_COLORS[p.kg]}`}>
                  {p.count}× {p.label}
                </span>
              ))}
            </div>
          )}
          <div className="bg-gold/10 rounded p-2 text-[11px] text-foreground/70">
            💡 Les plates sont à mettre de façon symétrique des deux côtés
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs flex-1 font-body normal-case" onClick={copyPlates}>
              <Copy size={12} className="mr-1" /> Copier
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ── Bar Table ─────────────────────────────────── */
function BarTable({ bar, pr }: { bar: typeof BARS[0]; pr: number }) {
  const [mode, setMode] = useState<"pct" | "fixed">("pct");

  const pctResults = useMemo(() => {
    if (pr <= 0) return null;
    return PCT_COLS.map((pct) => {
      const target = Math.round(pr * pct / 100 * 10) / 10;
      return { pct, result: suggestLoad(target, bar.kg) };
    });
  }, [pr, bar.kg]);

  const fixedResults = useMemo(() => {
    return FIXED_COLS.map((kg) => ({ kg, result: suggestLoad(kg, bar.kg) }));
  }, [bar.kg]);

  return (
    <div className={`bg-card border-2 ${bar.accent} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div className={`${bar.accentBg} px-5 py-4 flex flex-wrap items-center justify-between gap-3`}>
        <div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${bar.accentBg} ${bar.accentText} border ${bar.accent} font-body normal-case`}>
            {bar.tag}
          </span>
          <h3 className="text-xl text-foreground mt-1">{bar.label}</h3>
        </div>
        <div className="flex rounded-md border border-border overflow-hidden">
          <button
            onClick={() => setMode("pct")}
            className={`px-3 py-1.5 text-xs font-body normal-case transition-colors ${mode === "pct" ? "bg-gold text-gold-foreground" : "bg-card text-muted-foreground hover:bg-muted"}`}
          >
            % du PR
          </button>
          <button
            onClick={() => setMode("fixed")}
            className={`px-3 py-1.5 text-xs font-body normal-case transition-colors ${mode === "fixed" ? "bg-gold text-gold-foreground" : "bg-card text-muted-foreground hover:bg-muted"}`}
          >
            Charges fixes
          </button>
        </div>
      </div>

      <div className="p-4">
        {mode === "pct" ? (
          pr <= 0 ? (
            <p className="text-sm text-muted-foreground font-body normal-case text-center py-6">
              Renseigne ton PR ci-dessus pour afficher le tableau des pourcentages
            </p>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 pb-2">
              <div className="flex gap-1.5 min-w-max">
                {pctResults!.map(({ pct, result }) => (
                  <div key={pct} className="flex flex-col items-center gap-1">
                    <span className="text-[11px] text-muted-foreground font-body">{pct}%</span>
                    <PlateCell result={result} barKg={bar.kg} />
                  </div>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <div className="flex gap-1.5 min-w-max">
              {fixedResults.map(({ kg, result }) => (
                <div key={kg} className="flex flex-col items-center gap-1">
                  <span className="text-[11px] text-muted-foreground font-body">{kg}kg</span>
                  <PlateCell result={result} barKg={bar.kg} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────── */
export default function ChargesPage() {
  const [pr, setPR] = useState(0);
  const [prInput, setPrInput] = useState("");

  const handleUsePR = useCallback((value: number) => {
    setPR(value);
    setPrInput(String(value));
  }, []);

  const handlePrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setPrInput(v);
    const n = parseFloat(v);
    setPR(isNaN(n) || n < 0 ? 0 : n);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Calculateur PR (1RM) & charges CrossFit | Chistera"
        description="Estime ton PR (1RM), calcule tes pourcentages de travail (50-95 %) et visualise la répartition des plates sur barres 20/15/10 kg. Outil gratuit."
        canonical="https://crossfitchistera.fr/charges"
        ogImages={SERP_IMAGE_URLS}
        jsonLd={[
          buildSerpImageGraph(),
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Calculateur de charges CrossFit & PR — CrossFit Chistera",
            url: "https://crossfitchistera.fr/charges",
            applicationCategory: "HealthApplication",
            operatingSystem: "Any",
            description:
              "Calculateur 1RM (Epley, Brzycki, Lombardi, O'Conner), pourcentages de PR et répartition des plates pour barres 20/15/10 kg.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            inLanguage: "fr-FR",
            publisher: { "@type": "Organization", name: "CrossFit Chistera", url: "https://crossfitchistera.fr/" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://crossfitchistera.fr/" },
              { "@type": "ListItem", position: 2, name: "Calculateur de charges & PR", item: "https://crossfitchistera.fr/charges" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Qu'est-ce qu'un PR (1RM) en CrossFit ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Le PR (Personal Record) ou 1RM (One Repetition Maximum) est la charge maximale qu'un athlète peut soulever sur une seule répétition d'un mouvement (back squat, deadlift, clean, snatch, strict press, etc.) avec une technique propre. C'est la référence utilisée en CrossFit pour calibrer les charges de travail en pourcentages.",
                },
              },
              {
                "@type": "Question",
                name: "Quelle est la différence entre un PR et un RM ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Un RM (Repetition Maximum) est la charge maximale qu'on peut soulever sur un nombre donné de répétitions : 1RM, 3RM, 5RM, 10RM. Le PR (Personal Record) est par défaut le 1RM, mais le terme s'utilise aussi pour désigner ton record personnel sur n'importe quel format (5RM, AMRAP, Fran, etc.). En clair : tout PR sur une rep max est un 1RM, mais tous les PR ne sont pas des 1RM.",
                },
              },
              {
                "@type": "Question",
                name: "Comment évaluer précisément mon PR sans tester un vrai 1RM ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Le plus fiable est de réaliser une série propre entre 3 et 6 répétitions à charge maximale (sans casser la technique, sans aide), puis d'appliquer une formule d'estimation (Epley, Brzycki, Lombardi, O'Conner). L'estimation reste précise jusqu'à 5-6 reps ; au-delà de 10 reps, l'erreur dépasse souvent 10 %. Pour un mouvement explosif d'haltérophilie (clean, snatch), la fiabilité chute après 3 reps : on préfère donc un test direct ou une série très courte. Source : revue de la NSCA — Journal of Strength and Conditioning Research, voir aussi les recommandations de l'ACSM.",
                },
              },
              {
                "@type": "Question",
                name: "Comment évolue mon PR au fil du temps ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Chez un débutant, le PR progresse rapidement (gains neuromusculaires, +20 à 40 % en quelques mois). Chez un pratiquant intermédiaire, la progression devient cyclique : 2,5 à 5 kg par cycle de 6 à 12 semaines selon le mouvement. Chez un athlète avancé, on parle de quelques kilos par an. Le PR fluctue aussi avec le sommeil, le stress, la nutrition et la fatigue accumulée. Il faut le retester après chaque cycle de force structuré (généralement tous les 2 à 4 mois), pas chaque semaine.",
                },
              },
              {
                "@type": "Question",
                name: "Dans quels cas mesurer en RPE plutôt qu'en pourcentage du PR ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Le RPE (Rate of Perceived Exertion, échelle de 1 à 10) est plus pertinent que le %PR dans plusieurs situations : 1) mouvements gymniques (strict pull-up, HSPU, muscle-up) où le PR exprimé en kg n'a pas de sens — un athlète qui fait 3 strict pull-ups max ne peut pas faire 60 % de cette valeur. 2) Mouvements techniques où la qualité prime sur la charge (snatch, clean & jerk en apprentissage). 3) Jours de fatigue ou de mauvaise forme : le RPE adapte la charge à la condition du jour. 4) Travail de volume et d'accessoires. Le %PR reste roi sur les mouvements de force lents (squat, deadlift, bench) chez un athlète régulier. Source : méthode RTS de Mike Tuchscherer, largement reprise par la NSCA.",
                },
              },
              {
                "@type": "Question",
                name: "Comment calculer mes poids de travail à partir de mon PR ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "On applique un pourcentage du PR selon l'objectif de la séance : 50-65 % pour la technique et l'échauffement, 65-75 % pour l'hypertrophie et le volume (séries de 6 à 10 reps), 75-85 % pour la force (séries de 3 à 6 reps), 85-95 % pour la force max (séries de 1 à 3 reps), 95 %+ pour les essais de PR. Notre calculateur fait le calcul automatiquement et arrondit à la combinaison de plates réellement disponible à la box. Référence : Prilepin's Chart, utilisée historiquement en haltérophilie.",
                },
              },
              {
                "@type": "Question",
                name: "Quelles différences entre les formules Epley, Brzycki, Lombardi et O'Conner ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Epley (1RM = poids × (1 + reps/30)) est la plus connue, fiable sur 3 à 10 reps, légèrement optimiste sur les séries longues. Brzycki (1RM = poids × 36/(37 - reps)) est la plus précise sur les séries courtes (1 à 6 reps) et les mouvements lents (back squat, bench press, strict press). Lombardi (1RM = poids × reps^0,10) est plus prudente sur les séries longues (8 à 15 reps), utile en hypertrophie. O'Conner (1RM = poids × (1 + reps/40)) sous-estime légèrement, ce qui la rend adaptée aux mouvements explosifs d'haltérophilie (clean, snatch, jerk) où la marge de sécurité prime. En pratique : croiser deux formules et prendre la moyenne donne le résultat le plus stable. Source : revue comparative LeSuer et al., Journal of Strength and Conditioning Research.",
                },
              },
              {
                "@type": "Question",
                name: "Comment travailler efficacement au CrossFit ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Trois principes : 1) Régularité — trois séances par semaine pendant six mois battent six séances par semaine pendant un mois. 2) Progressivité — augmenter charge ou volume de 2,5 à 5 % par semaine maximum sur les mouvements de force, accepter les semaines de décharge tous les 4 à 6 cycles. 3) Technique avant intensité — un mouvement mal exécuté à charge lourde ne renforce rien et expose à la blessure. Concrètement : connaître son PR pour calibrer les charges, respecter le scaling proposé par le coach, dormir 7 à 9 h, manger suffisamment de protéines (1,6 à 2 g/kg/jour). Référence : recommandations de l'OMS sur l'activité physique et de la NSCA pour la programmation force.",
                },
              },
              {
                "@type": "Question",
                name: "Comment charger une barre symétriquement ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Les plates doivent toujours être chargées de façon symétrique des deux côtés de la barre, en commençant par les disques les plus lourds contre la barre. Le calculateur indique exactement les disques à mettre par côté (20, 15, 10, 5, 2,5 et 1 kg) à partir de la cible théorique, et arrondit à la combinaison réellement disponible à la box.",
                },
              },
            ],
          },
        ]}
      />
      <Header />
      <main className="pt-24 pb-20">
        <div className="container max-w-[1200px] mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline font-body normal-case mb-6">
            <ArrowLeft size={16} /> Revenir à l'accueil
          </Link>

          <div className="text-center mb-4">
            <h1 className="text-4xl md:text-5xl text-foreground">Mes charges / PR</h1>
            <p className="text-muted-foreground mt-3 font-body normal-case max-w-2xl mx-auto">
              Calcule tes charges d'entraînement à partir de ton PR et visualise la répartition exacte des plates* à charger sur la barre
            </p>
          </div>

          {/* How it works */}
          <div className="bg-gold/5 border border-gold/15 rounded-lg p-5 mb-10 max-w-2xl mx-auto">
            <h3 className="text-lg text-foreground mb-2">Comment ça marche ?</h3>
            <ol className="text-sm text-muted-foreground font-body normal-case space-y-1.5 list-decimal list-inside">
              <li><strong className="text-foreground">Estime ton PR</strong> : renseigne un poids et un nombre de reps, la formule calcule ton 1RM</li>
              <li><strong className="text-foreground">Consulte les tableaux</strong> : chaque barre affiche les charges en % du PR ou en charges fixes</li>
              <li><strong className="text-foreground">Clique sur une cellule</strong> : tu verras exactement quelles plates mettre de chaque côté</li>
              <li>La charge proposée est toujours <strong className="text-foreground">arrondie au-dessus</strong> avec les plates disponibles à la box</li>
            </ol>
          </div>

          {/* PR Calculator */}
          <PRCalculator onUsePR={handleUsePR} />

          {/* PR input for tables */}
          <div className="flex flex-wrap items-end gap-4 mb-8">
            <div>
              <label className="text-xs font-medium text-muted-foreground font-body normal-case mb-1 block">PR actuel (kg) — utilisé pour les %</label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={prInput}
                onChange={handlePrChange}
                placeholder="Ex : 100"
                className="w-40"
              />
            </div>
            {pr > 0 && (
              <Button variant="ghost" size="sm" className="font-body normal-case text-muted-foreground" onClick={() => { setPR(0); setPrInput(""); }}>
                <RotateCcw size={14} className="mr-1" /> Réinitialiser
              </Button>
            )}
          </div>

          {/* Bar tables */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl text-foreground">Calculer mes charges</h2>
            <p className="text-muted-foreground mt-2 font-body normal-case max-w-2xl mx-auto text-sm">
              En fonction de votre PR, cliquez sur la charge correspondante pour voir la répartition des plates à mettre sur la barre
            </p>
          </div>
          <div className="space-y-8 mb-16">
            {BARS.map((bar) => (
              <BarTable key={bar.kg} bar={bar} pr={pr} />
            ))}
          </div>

          {/* Available plates reminder */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl text-foreground mb-4">Plates disponibles à la box</h3>
            <div className="flex flex-wrap gap-3">
              {PLATES.map((p) => (
                <div key={p.kg} className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${PLATE_COLORS[p.kg]}`}>
                  {p.label} <span className="opacity-70">({p.color})</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground font-body normal-case mt-3">
              Les plates sont toujours chargées de façon symétrique des deux côtés de la barre
            </p>
            <p className="text-xs text-muted-foreground font-body normal-case mt-4 italic">
              * « Plates » ou Bumper plates correspond aux charges, sous forme de disque, que vous mettez sur votre barre
            </p>
          </div>

          {/* FAQ */}
          <section className="mt-16" aria-labelledby="faq-charges-title">
            <div className="text-center mb-8">
              <h2 id="faq-charges-title" className="text-2xl md:text-3xl text-foreground">Questions fréquentes sur le PR, le 1RM et les charges</h2>
              <p className="text-muted-foreground mt-2 font-body normal-case max-w-2xl mx-auto text-sm">
                Tout ce qu'il faut comprendre pour utiliser ce calculateur et structurer ton entraînement
              </p>
            </div>

            <Accordion type="single" collapsible className="max-w-3xl mx-auto bg-card border border-border rounded-lg px-4 md:px-6">
              <AccordionItem value="q1">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Qu'est-ce qu'un PR (1RM) en CrossFit ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Le <strong className="text-foreground">PR</strong> (Personal Record) ou <strong className="text-foreground">1RM</strong> (One Repetition Maximum) est la charge maximale qu'un athlète peut soulever sur une seule répétition d'un mouvement (back squat, deadlift, clean, snatch, strict press…) avec une technique propre. C'est la référence utilisée en CrossFit pour calibrer les charges de travail en pourcentages.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q2">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Quelle différence entre un PR et un RM ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Un <strong className="text-foreground">RM</strong> (Repetition Maximum) est la charge maximale qu'on peut soulever sur un nombre donné de répétitions : 1RM, 3RM, 5RM, 10RM. Le <strong className="text-foreground">PR</strong> est par défaut le 1RM, mais le terme désigne aussi ton record personnel sur n'importe quel format (5RM, AMRAP, Fran…). En clair : tout PR sur une rep max est un 1RM, mais tous les PR ne sont pas des 1RM.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q3">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Comment évaluer précisément mon PR sans tester un vrai 1RM ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Le plus fiable est de réaliser une série propre entre <strong className="text-foreground">3 et 6 répétitions</strong> à charge maximale (sans casser la technique, sans aide), puis d'appliquer une formule d'estimation (Epley, Brzycki, Lombardi, O'Conner). L'estimation reste précise jusqu'à 5-6 reps ; au-delà de 10 reps, l'erreur dépasse souvent 10 %.</p>
                  <p>Pour un mouvement explosif d'haltérophilie (clean, snatch), la fiabilité chute après 3 reps : on préfère un test direct ou une série très courte.</p>
                  <p className="text-xs">Sources : <a href="https://journals.lww.com/nsca-jscr/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">NSCA — Journal of Strength and Conditioning Research</a>, <a href="https://www.acsm.org/education-resources/trending-topics-resources/physical-activity-guidelines" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">ACSM Guidelines</a>.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q4">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Comment évolue mon PR au fil du temps ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Chez un <strong className="text-foreground">débutant</strong>, le PR progresse rapidement (gains neuromusculaires, +20 à 40 % en quelques mois). Chez un <strong className="text-foreground">intermédiaire</strong>, la progression devient cyclique : 2,5 à 5 kg par cycle de 6 à 12 semaines selon le mouvement. Chez un <strong className="text-foreground">avancé</strong>, on parle de quelques kilos par an.</p>
                  <p>Le PR fluctue aussi avec le sommeil, le stress, la nutrition et la fatigue accumulée. On le retest après chaque cycle de force structuré (tous les 2 à 4 mois), pas chaque semaine.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q5">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Dans quels cas mesurer en RPE plutôt qu'en % du PR ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Le <strong className="text-foreground">RPE</strong> (Rate of Perceived Exertion, échelle 1 à 10) est plus pertinent que le %PR dans plusieurs cas :</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong className="text-foreground">Mouvements gymniques</strong> (strict pull-up, HSPU, muscle-up) où le PR exprimé en kg n'a pas de sens. Si ton max strict pull-up est 3 reps, calculer 60 ou 75 % de cette valeur n'a aucune signification — le RPE est la seule mesure exploitable.</li>
                    <li><strong className="text-foreground">Mouvements techniques</strong> où la qualité prime sur la charge (snatch, clean & jerk en apprentissage).</li>
                    <li><strong className="text-foreground">Jours de fatigue</strong> ou de mauvaise forme : le RPE adapte la charge à la condition du jour.</li>
                    <li><strong className="text-foreground">Travail de volume et accessoires.</strong></li>
                  </ul>
                  <p>Le %PR reste roi sur les mouvements de force lents (squat, deadlift, bench) chez un athlète régulier.</p>
                  <p className="text-xs">Source : <a href="https://www.reactivetrainingsystems.com/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">méthode RTS de Mike Tuchscherer</a>, largement reprise par la NSCA.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q6">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Comment calculer mes poids de travail à partir de mon PR ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>On applique un pourcentage du PR selon l'objectif de la séance :</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong className="text-foreground">50 – 65 %</strong> : technique et échauffement</li>
                    <li><strong className="text-foreground">65 – 75 %</strong> : hypertrophie et volume (6 à 10 reps)</li>
                    <li><strong className="text-foreground">75 – 85 %</strong> : force (3 à 6 reps)</li>
                    <li><strong className="text-foreground">85 – 95 %</strong> : force max (1 à 3 reps)</li>
                    <li><strong className="text-foreground">95 % +</strong> : essais de PR</li>
                  </ul>
                  <p>Notre calculateur fait le calcul automatiquement et arrondit à la combinaison de plates réellement disponible à la box. Référence historique : <a href="https://en.wikipedia.org/wiki/Prilepin%27s_chart" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Prilepin's Chart</a>, utilisée en haltérophilie soviétique.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q7">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Quelles différences entre Epley, Brzycki, Lombardi et O'Conner ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <ul className="list-disc pl-5 space-y-1.5">
                    <li><strong className="text-foreground">Epley</strong> — <em>1RM = poids × (1 + reps/30)</em>. La plus connue, fiable de 3 à 10 reps, légèrement optimiste sur les séries longues.</li>
                    <li><strong className="text-foreground">Brzycki</strong> — <em>1RM = poids × 36/(37 − reps)</em>. La plus précise sur les séries courtes (1 à 6 reps) et les mouvements lents (back squat, bench, strict press).</li>
                    <li><strong className="text-foreground">Lombardi</strong> — <em>1RM = poids × reps^0,10</em>. Plus prudente sur les séries longues (8 à 15 reps), utile en hypertrophie.</li>
                    <li><strong className="text-foreground">O'Conner</strong> — <em>1RM = poids × (1 + reps/40)</em>. Sous-estime légèrement, donc adaptée aux mouvements explosifs d'haltérophilie (clean, snatch, jerk) où la marge de sécurité prime.</li>
                  </ul>
                  <p>En pratique : croiser deux formules et prendre la moyenne donne le résultat le plus stable.</p>
                  <p className="text-xs">Source : <a href="https://pubmed.ncbi.nlm.nih.gov/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">LeSuer et al., Journal of Strength and Conditioning Research (1997)</a> — comparaison des formules d'estimation du 1RM.</p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="q8">
                <AccordionTrigger className="text-left text-foreground font-body normal-case">
                  Comment travailler efficacement au CrossFit ?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-2">
                  <p>Trois principes structurants :</p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li><strong className="text-foreground">Régularité</strong> — trois séances par semaine pendant six mois battent six séances par semaine pendant un mois.</li>
                    <li><strong className="text-foreground">Progressivité</strong> — augmenter charge ou volume de 2,5 à 5 % par semaine maximum sur les mouvements de force, accepter une semaine de décharge tous les 4 à 6 cycles.</li>
                    <li><strong className="text-foreground">Technique avant intensité</strong> — un mouvement mal exécuté à charge lourde ne renforce rien et expose à la blessure.</li>
                  </ol>
                  <p>Concrètement : connaître son PR pour calibrer les charges, respecter le scaling proposé par le coach, dormir 7 à 9 h, viser 1,6 à 2 g de protéines / kg / jour.</p>
                  <p className="text-xs">Références : <a href="https://www.who.int/fr/news-room/fact-sheets/detail/physical-activity" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">OMS — Activité physique</a>, <a href="https://www.nsca.com/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">NSCA — Strength & Conditioning</a>, <a href="https://journal.crossfit.com/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">CrossFit Journal</a>.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>

          {/* SEO context block */}
          <section className="mt-16 max-w-3xl mx-auto" aria-labelledby="seo-context-title">
            <h2 id="seo-context-title" className="text-2xl md:text-3xl text-foreground text-center mb-4">À quoi sert ce calculateur ?</h2>
            <div className="text-muted-foreground font-body normal-case text-sm leading-relaxed space-y-3">
              <p>
                Cette page rassemble en un seul endroit les trois outils dont un pratiquant de CrossFit a besoin au quotidien :
                un <strong className="text-foreground">estimateur de 1RM</strong> à partir d'une série réalisée (les quatre formules de référence — Epley,
                Brzycki, Lombardi, O'Conner — sont disponibles et adaptées automatiquement au mouvement choisi), un
                <strong className="text-foreground"> tableau de pourcentages</strong> qui convertit ton PR en charge de travail
                pour chaque intensité (force, hypertrophie, volume, technique), et un
                <strong className="text-foreground"> calculateur de plates</strong> qui te dit exactement quels disques mettre de chaque
                côté de la barre, en tenant compte du matériel réellement disponible à la box.
              </p>
              <p>
                Le calculateur est gratuit, sans inscription, et fonctionne sur mobile pendant la séance. Il est conçu et
                maintenu par les coachs de <strong className="text-foreground">CrossFit Chistera</strong> à Saint-Pierre-de-Chandieu (Est lyonnais),
                à partir des références reconnues en force et conditionnement (NSCA, ACSM, CrossFit Journal). Il sert à la fois
                aux athlètes de la box pour préparer leur séance, et à tout pratiquant qui cherche un outil fiable, transparent
                sur ses formules, sans publicité ni revente de données.
              </p>
              <p>
                Si tu débutes le CrossFit ou si tu reprends après une pause, la <Link to="/blog/crossfit-debutant-est-lyonnais" className="text-gold hover:underline">page débutant</Link>
                {" "}explique comment se déroule une première séance. Pour comprendre la programmation, le scaling et la progression,
                voir le <Link to="/blog/guide-complet-crossfit" className="text-gold hover:underline">guide complet du CrossFit</Link>.
              </p>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
